/* eslint-disable react/jsx-pascal-case */
import {
  fetchSummary,
  fetchTransactionsCSV,
  fetchTransactionsV2,
} from 'admin/actions/transactions';
import { Button } from 'admin/components/atoms/Buttons';
import FA from 'admin/components/atoms/FontAwesome';
import BreadCrumb from 'admin/components/molecules/BreadCrumb';
import { downloadCsvFile, toPipeDelimitedQueryParams, toQueryParams } from 'admin/utils';
import moment, { Moment } from 'moment';
import React, { ComponentProps, useCallback, useEffect, useState } from 'react';
import Loader from 'react-loader-spinner';
import styled from 'styled-components';
import {
  BooleanParam,
  decodeDelimitedArray,
  encodeDelimitedArray,
  ObjectParam,
  QueryParamConfig,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { fetchTransfersCSV } from '../../../actions/transfers';
import { useAppSelector } from '../../../hooks/useApp';
import { selectUserPrivateMoney } from '../../../reducers/user-private-money';
import ConfirmModal from '../../atoms/ConfirmModal';
import FilterForm from './FilterForm';
import FilteringResult from './FilterResult';
import ListTable from './ListTable';
import Summary from './Summary';

type Summary = ComponentProps<typeof Summary>['summary'];

const FormWrapper = styled.div``;
const HeaderWrapper = styled.div`
  display: flex;
  ${Button} {
    margin-right: 10px;
    height: 70%;
  }
`;
const FormTextWrapper = styled.span`
  padding: 1px;
  margin: 5px;
`;

const LoaderWrapper = styled.div`
  background: #f5f5f5;
  text-align: center;
  padding: 1rem;
`;

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '取引管理', href: '', isCurrentPage: true },
  { value: '取引一覧', href: '', isCurrentPage: true },
];

const removeEmptyParams = (params: Record<PropertyKey, unknown>) =>
  Object.keys(params).reduce((acc, key) => {
    if (params[key] || params[key] === false) {
      acc[key] = params[key];
    }
    return acc;
  }, {});

const MomentParam: QueryParamConfig<Moment | null | undefined> = {
  encode: (value) => (value ? value.toISOString() : null),
  decode: (value) => (value && typeof value === 'string' ? moment(value) : null),
};

// ref: https://github.com/pbeshai/use-query-params/blob/master/packages/use-query-params/README.md
// Example with Custom Param
const PipeDelimitedArrayParam = {
  encode: (array: string[] | null | undefined) => encodeDelimitedArray(array, '|'),
  decode: (arrayStr: string | string[] | null | undefined) => decodeDelimitedArray(arrayStr, '|'),
};

type TransactionsQueryParamConfig = typeof queryParamConfigs;
export type TransactionsQueryParams = {
  [key in keyof TransactionsQueryParamConfig]: TransactionsQueryParamConfig[key] extends QueryParamConfig<
    unknown,
    infer T
  >
    ? T
    : never;
};

const queryParamConfigs = {
  organization: withDefault(ObjectParam, null),
  shop: withDefault(ObjectParam, null),
  terminal: withDefault(ObjectParam, null),
  transactionTypes: withDefault(PipeDelimitedArrayParam, null),
  topupMethodTypes: withDefault(PipeDelimitedArrayParam, null),
  isModified: withDefault(BooleanParam, null),
  from: withDefault(MomentParam, null),
  to: withDefault(MomentParam, null),
  transactionId: withDefault(StringParam, ''),
  customerId: withDefault(StringParam, ''),
  customerName: withDefault(StringParam, ''),
  description: withDefault(StringParam, ''),
  excludeCashbackAndExpire: withDefault(BooleanParam, true),
};

const Transactions = () => {
  const userPrivateMoney = useAppSelector(selectUserPrivateMoney);
  const isFromClearing = window.location.pathname === '/clearing/transactions';

  const clearingDetailsParams = useAppSelector((state) => state.transaction.listForm);

  const [query, setQuery] = useQueryParams({
    ...queryParamConfigs,
  });

  const [transactions, setTransactions] =
    useState<Awaited<ReturnType<typeof fetchTransactionsV2>>>();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const [summary, setSummary] = useState<Summary>();

  const [isLoading, setIsLoading] = useState(false);

  const getQueryParams = useCallback(() => {
    const {
      organization,
      shop,
      terminal,
      excludeCashbackAndExpire,
      transactionTypes,
      topupMethodTypes,
      ...filterRest
    } = query;

    let updatedTransactionTypes: (string | null)[] | null = null;
    let updatedTopupMethodTypes: (string | null)[] | null = null;

    if (isFromClearing) {
      updatedTransactionTypes = clearingDetailsParams.transactionTypes;
      updatedTopupMethodTypes = clearingDetailsParams.topupMethodTypes;
    } else if ((transactionTypes?.length ?? 0) > 0) {
      updatedTransactionTypes = transactionTypes;
      if (updatedTransactionTypes?.some((x) => x === 'topup')) {
        updatedTopupMethodTypes = topupMethodTypes;
      } else {
        updatedTopupMethodTypes = null;
      }
    } else if (excludeCashbackAndExpire) {
      // the API cannot exclude specific transaction types, and by default we want to exclude cashback and expire
      // so as a workaround, we send all transaction types not including cashback and expire
      updatedTransactionTypes = ['payment', 'topup', 'exchange_outflow', 'exchange_inflow'];
      updatedTopupMethodTypes = null;
    }

    // transactionTypes needs to be sent to the api in the format of transaction_types[]: x | y | z,
    // which is why the encoded value is wrapped in an array eventhough it is a string ¯\_(ツ)_/¯
    const params = isFromClearing
      ? {
          privateMoney: clearingDetailsParams.privateMoney,
          from: clearingDetailsParams.from,
          to: clearingDetailsParams.to,
          organization: clearingDetailsParams.organization,
          transactionTypes: updatedTransactionTypes
            ? [encodeDelimitedArray(updatedTransactionTypes, '|')]
            : null,
          topupMethodTypes: updatedTopupMethodTypes
            ? [encodeDelimitedArray(updatedTopupMethodTypes, '|')]
            : null,
        }
      : {
          privateMoney: userPrivateMoney.id,
          ...filterRest,
          organization: organization?.code,
          shopId: shop?.id,
          terminalId: terminal?.id,
          transactionTypes: updatedTransactionTypes
            ? [encodeDelimitedArray(updatedTransactionTypes, '|')]
            : null,
          topupMethodTypes: updatedTopupMethodTypes
            ? [encodeDelimitedArray(updatedTopupMethodTypes, '|')]
            : null,
        };

    return params;
  }, [query, userPrivateMoney, clearingDetailsParams, isFromClearing]);

  const updateTransactions = useCallback(
    (queryLink?: string) => {
      fetchTransactionsV2(queryLink || toQueryParams(removeEmptyParams(getQueryParams()))).then(
        (tr) => {
          setTransactions(tr);
        }
      );
    },
    [getQueryParams]
  );

  const updateSummary = useCallback(() => {
    const { transactionTypes, ...summaryParams } = getQueryParams();
    fetchSummary(toQueryParams(removeEmptyParams(summaryParams))).then((res) => {
      const typeAdjustedSummary = (query.transactionTypes ?? ['payment', 'topup']).reduce(
        (acc, type) => {
          if (type === 'payment') {
            return {
              ...acc,
              payment: res.summary.payment,
            };
          }
          if (type === 'topup') {
            return {
              ...acc,
              topup: res.summary.topup,
            };
          }
          return acc;
        },
        {
          payment: { count: 0, money: 0, point: 0, raw_point: 0, campaign_point: 0 },
          topup: { count: 0, money: 0, point: 0, raw_point: 0, campaign_point: 0 },
        }
      );

      setSummary(typeAdjustedSummary);
    });
  }, [getQueryParams]);

  useEffect(() => {
    updateTransactions();
    updateSummary();
  }, [updateTransactions, updateSummary]);

  const onConfirmFilter = (params: TransactionsQueryParams) => {
    setQuery(params);
    setIsFilterModalOpen(false);
  };

  const onChangePage = (paginatedQuery: string | null) =>
    updateTransactions(paginatedQuery ?? undefined);

  const fetchTransactionForCSV = useCallback(() => {
    const params = {
      ...getQueryParams(),
      perPage: 10000,
      maxPage: 30,
    };
    setIsLoading(true);
    fetchTransactionsCSV(toQueryParams(removeEmptyParams(params)))
      .then((ret) => {
        downloadCsvFile(ret.url);
      })
      .finally(() => setIsLoading(false));
  }, [getQueryParams]);

  const fetchTransferForCSV = () => {
    // TODO transactionも全てid付の名前に修正する
    setIsLoading(true);

    const params = {
      description: '',
      ...getQueryParams(),
      perPage: 10000,
      maxPage: 30,
    };

    const { privateMoney, organization, description, ...paramRest } = params;

    const transferParams = {
      privateMoneyId: privateMoney,
      organizationCode: organization,
      transactionDescription: description,
      ...paramRest,
    };

    fetchTransfersCSV(toPipeDelimitedQueryParams(removeEmptyParams(transferParams)))
      .then((ret) => {
        downloadCsvFile(ret.url);
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <FormWrapper>
      <BreadCrumb crumbs={CRUMBS} />
      {isLoading ? (
        <LoaderWrapper>
          <Loader type="Oval" height={50} width={50} />
        </LoaderWrapper>
      ) : (
        <HeaderWrapper>
          {!isFromClearing && (
            <Button onClick={() => setIsFilterModalOpen(true)}>条件で絞り込む</Button>
          )}
          <Button
            onClick={() => {
              updateTransactions();
              updateSummary();
            }}
          >
            更新
          </Button>
          <Button onClick={fetchTransactionForCSV} variant="muted-outline">
            <FA icon="download" /> 取引CSV
          </Button>
          <Button onClick={fetchTransferForCSV} variant="muted-outline">
            <FA icon="download" /> 取引詳細CSV
          </Button>
          <FormTextWrapper>
            件数：{(summary?.payment.count ?? 0) + (summary?.topup.count ?? 0)}{' '}
            (最大30万件までダウンロードできます)
          </FormTextWrapper>
        </HeaderWrapper>
      )}
      {isFromClearing ? (
        <FilteringResult isClearing params={clearingDetailsParams} />
      ) : (
        <FilteringResult isClearing={false} params={query} privateMoney={userPrivateMoney} />
      )}
      <Summary summary={summary} />
      <ListTable
        transactions={transactions}
        privateMoneyId={userPrivateMoney.id}
        onChangePage={onChangePage}
      />
      <ConfirmModal
        isModalOpen={isFilterModalOpen}
        closeModal={() => setIsFilterModalOpen(false)}
        title="絞り込み条件の設定"
        form={
          <FilterForm
            onConfirm={onConfirmFilter}
            closeModal={() => setIsFilterModalOpen(false)}
            filterParams={query}
          />
        }
      />
    </FormWrapper>
  );
};

export default Transactions;
